import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authFetch, readJSON } from '../lib/auth';
import { useAuth } from '../contexts/AuthContext';
import { Users, Sparkles, TrendingUp, Crown, ArrowLeft, RefreshCw, Star, MessageSquare, IndianRupee, Download, Activity, ArrowRight, FileSpreadsheet, Mail, Save, Clock, PartyPopper, Play } from 'lucide-react';
import CelebrationOverlay from '../components/CelebrationOverlay';

// Convert any ISO / JS Date to India Standard Time "YYYY-MM-DD HH:mm".
// Used everywhere on the admin dashboard so times reflect the owner's timezone.
const toIST = (raw) => {
  if (!raw) return '—';
  try {
    const d = raw instanceof Date ? raw : new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    const p = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).reduce((o, x) => ((o[x.type] = x.value), o), {});
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
  } catch {
    return String(raw);
  }
};

// IST date only (YYYY-MM-DD)
const toISTDate = (raw) => (toIST(raw).split(' ')[0] || '—');

function StatCard({ icon: Icon, label, value, accent = 'amber' }) {
  const accents = {
    amber: 'from-amber-500/20 to-orange-500/10 text-amber-300 border-amber-500/30',
    cyan: 'from-cyan-500/20 to-sky-500/10 text-cyan-300 border-cyan-500/30',
    violet: 'from-violet-500/20 to-purple-500/10 text-violet-300 border-violet-500/30',
    emerald: 'from-emerald-500/20 to-teal-500/10 text-emerald-300 border-emerald-500/30',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${accents[accent]} backdrop-blur-sm p-4 shadow-xl`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80 mb-1">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

// Live pricing editor — owner can tweak prices & limits without a redeploy.
function PricingEditor({ initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const numericFields = [
    { k: 'starter_monthly_inr', label: 'Starter / month (₹)', step: '1' },
    { k: 'starter_monthly_usd', label: 'Starter / month ($)', step: '0.01' },
    { k: 'starter_exports_per_month', label: 'Starter exports / month', step: '1' },
    { k: 'pro_monthly_inr', label: 'Pro / month (₹)', step: '1' },
    { k: 'pro_monthly_usd', label: 'Pro / month ($)', step: '0.01' },
    { k: 'pro_exports_per_month', label: 'Pro exports / month', step: '1' },
    { k: 'pro_yearly_inr', label: 'Pro / year (₹)', step: '1' },
    { k: 'pro_yearly_usd', label: 'Pro / year ($)', step: '0.01' },
    { k: 'free_max_projects', label: 'Free projects (0 = unlimited)', step: '1' },
    { k: 'free_exports_per_month', label: 'Free exports / month', step: '1' },
  ];

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const payload = {};
      numericFields.forEach(({ k }) => {
        const v = Number(form[k]);
        if (!Number.isNaN(v)) payload[k] = v;
      });
      payload.free_ai_enabled = !!form.free_ai_enabled;

      // Client-side minimum-price safety (matches backend)
      const priceErrors = [];
      numericFields.forEach(({ k, label }) => {
        const v = Number(form[k]);
        if (Number.isNaN(v)) return;
        if (k.endsWith('_inr') && !k.includes('exports') && !k.includes('projects') && v < 10) {
          priceErrors.push(`${label}: must be ≥ ₹10`);
        }
        if (k.endsWith('_usd') && v < 0.5) {
          priceErrors.push(`${label}: must be ≥ $0.50`);
        }
      });
      if (priceErrors.length > 0) {
        setMsg(`Rejected: ${priceErrors[0]}`);
        setSaving(false);
        setTimeout(() => setMsg(''), 5000);
        return;
      }

      // Show old → new diff for every changed field
      const diffLines = [];
      numericFields.forEach(({ k, label }) => {
        const oldV = initial?.[k];
        const newV = Number(form[k]);
        if (!Number.isNaN(newV) && String(oldV) !== String(newV)) {
          diffLines.push(`  ${label}:  ${oldV}  →  ${newV}`);
        }
      });
      if ((!!initial?.free_ai_enabled) !== (!!form.free_ai_enabled)) {
        diffLines.push(`  Free AI:  ${initial?.free_ai_enabled ? 'On' : 'Off'}  →  ${form.free_ai_enabled ? 'On' : 'Off'}`);
      }
      if (diffLines.length === 0) {
        setMsg('No changes to save.');
        setSaving(false);
        setTimeout(() => setMsg(''), 3500);
        return;
      }
      const ok = window.confirm(
        `You're about to update these prices / limits:\n\n${diffLines.join('\n')}\n\nThis takes effect IMMEDIATELY for all customers.\nConfirm?`
      );
      if (!ok) {
        setSaving(false);
        return;
      }

      const res = await authFetch('/api/admin/pricing', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const updated = await readJSON(res);
      if (!res.ok) throw new Error(updated?.detail || 'Save failed');
      setMsg('Saved ✓ New prices are live on /pricing.');
      onSaved?.(updated);
    } catch (e) {
      setMsg(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 4000);
    }
  };

  return (
    <div
      className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5 shadow-xl"
      data-testid="admin-pricing-editor"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-300">Plans & Pricing · editable</h2>
        {msg && (
          <span
            className={`text-xs ${msg.startsWith('Saved') ? 'text-emerald-400' : 'text-rose-400'}`}
            data-testid="admin-pricing-msg"
          >
            {msg}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {numericFields.map(({ k, label, step }) => (
          <label key={k} className="block text-xs">
            <span className="text-slate-400">{label}</span>
            <input
              type="number"
              step={step}
              value={form[k] ?? ''}
              onChange={(e) => update(k, e.target.value)}
              className="mt-1 w-full rounded bg-slate-900/60 border border-slate-700 text-white px-2 py-1.5 text-sm focus:outline-none focus:border-cyan-500"
              data-testid={`admin-pricing-input-${k}`}
            />
          </label>
        ))}
        <label className="flex items-center gap-2 text-xs text-slate-400 col-span-2 md:col-span-3 pt-1">
          <input
            type="checkbox"
            checked={!!form.free_ai_enabled}
            onChange={(e) => update('free_ai_enabled', e.target.checked)}
            className="rounded bg-slate-900 border-slate-700"
            data-testid="admin-pricing-input-free_ai_enabled"
          />
          Allow Free users to use AI Enhance (otherwise Pro-only)
        </label>
      </div>
      <div className="flex items-center justify-end mt-4">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-pricing-save-btn"
          className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium shadow-lg disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save prices'}
        </button>
      </div>
    </div>
  );
}

// CSV export buttons — Excel opens CSVs natively.
function ExportDataPanel() {
  const [downloading, setDownloading] = useState('');

  const download = async (kind, pretty) => {
    setDownloading(kind);
    try {
      const res = await authFetch(`/api/admin/export/${kind}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ts = new Date()
        .toLocaleString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false })
        .replace(/[/:, ]/g, '-');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${pretty}-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally {
      setDownloading('');
    }
  };

  const kinds = [
    { k: 'users', label: 'Users (full)', icon: Users },
    { k: 'emails', label: 'Emails only (for mailer)', icon: Mail },
    { k: 'payments', label: 'Payments', icon: IndianRupee },
    { k: 'sessions', label: 'Visitor sessions', icon: Activity },
    { k: 'feedback', label: 'Feedback', icon: MessageSquare },
  ];

  return (
    <div
      className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5 shadow-xl"
      data-testid="admin-export-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
          Extract data to Excel
        </h2>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">
          CSV · opens natively in Excel / Numbers / Sheets
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {kinds.map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            type="button"
            onClick={() => download(k, k)}
            disabled={downloading === k}
            data-testid={`admin-export-${k}`}
            className="flex items-center gap-2 bg-slate-900/60 hover:bg-slate-700/70 border border-slate-700 text-white px-3 py-2 rounded-lg text-xs disabled:opacity-50 transition-colors"
          >
            <Icon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span className="truncate">{downloading === k ? 'Downloading…' : label}</span>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mt-3">
        Tip: "Emails only" gives you a clean list you can paste into any mailer
        (Resend, Mailchimp, Gmail BCC) to send price updates or feature announcements.
      </p>
    </div>
  );
}

function CelebrationConfigCard() {
  const ANIMATIONS = [
    { key: 'confetti', label: 'Confetti burst', emoji: '🎉' },
    { key: 'snowflakes', label: 'Snowflakes', emoji: '❄️' },
    { key: 'fireworks', label: 'Fireworks', emoji: '🎆' },
    { key: 'hearts', label: 'Floating hearts', emoji: '💖' },
    { key: 'sparkles', label: 'Golden sparkles', emoji: '✨' },
    { key: 'off', label: 'Disabled (no animation)', emoji: '🚫' },
  ];
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    authFetch('/api/admin/celebration-config').then(readJSON).then((c) => {
      setCfg({
        animation: c?.animation || 'confetti',
        play_on_signup: !!c?.play_on_signup,
        enabled: c?.enabled !== false,
      });
    }).catch(() => {
      setCfg({ animation: 'confetti', play_on_signup: false, enabled: true });
    });
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    setStatus('');
    try {
      const res = await authFetch('/api/admin/celebration-config', {
        method: 'POST',
        body: JSON.stringify(cfg),
      });
      if (!res.ok) throw new Error('Save failed');
      setStatus('Saved ✓');
      setTimeout(() => setStatus(''), 2000);
    } catch (e) {
      setStatus(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;
  return (
    <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden" data-testid="admin-celebration-panel">
      {preview && preview !== 'off' && (
        <CelebrationOverlay animation={preview} onDone={() => setPreview(null)} />
      )}
      <div className="p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
            <PartyPopper className="w-4 h-4 text-amber-400" />
            Member celebration animation
          </h2>
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="celebration-enabled-toggle"
              className="accent-amber-500"
            />
            Enabled
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Plays once on the Pro purchase success screen. Pick an animation below — preview live before saving.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {ANIMATIONS.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => setCfg({ ...cfg, animation: a.key })}
              data-testid={`celebration-option-${a.key}`}
              className={`relative text-left rounded-lg border px-3 py-2 transition ${
                cfg.animation === a.key
                  ? 'border-amber-500/70 bg-amber-500/15 text-amber-200'
                  : 'border-slate-700 bg-slate-900/40 text-slate-300 hover:border-slate-500'
              }`}
            >
              <div className="text-base">{a.emoji}</div>
              <div className="text-[12px] mt-0.5">{a.label}</div>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setPreview(cfg.animation)}
            disabled={cfg.animation === 'off'}
            data-testid="celebration-preview-btn"
            className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-200 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-700"
          >
            <Play className="w-3.5 h-3.5" />
            Preview
          </button>

          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.play_on_signup}
              onChange={(e) => setCfg({ ...cfg, play_on_signup: e.target.checked })}
              data-testid="celebration-play-on-signup-toggle"
              className="accent-amber-500"
            />
            Also play on free sign-up
          </label>

          <div className="ml-auto flex items-center gap-3">
            {status && <span className="text-xs text-emerald-400">{status}</span>}
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="celebration-save-btn"
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs font-semibold px-4 py-1.5 rounded-md shadow"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState({ items: [], avg: 0, count: 0, distribution: {} });
  const [recentPayments, setRecentPayments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [usersTime, setUsersTime] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, u, f, p, sess, ut] = await Promise.all([
        authFetch('/api/admin/stats').then(readJSON),
        authFetch('/api/admin/users').then(readJSON),
        authFetch('/api/admin/feedback').then(readJSON),
        authFetch('/api/admin/recent-payments').then(readJSON),
        authFetch('/api/admin/sessions').then(readJSON),
        authFetch('/api/admin/users-time').then(readJSON),
      ]);
      setStats(s);
      setUsers(u.users || []);
      setFeedback({
        items: f.items || [],
        avg: f.avg || 0,
        count: f.count || 0,
        distribution: f.distribution || {},
      });
      setRecentPayments(p.items || []);
      setSessions(sess.items || []);
      setUsersTime(ut.items || []);
    } catch (e) {
      setError(e.message || 'Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePro = async (uid, currentPlan) => {
    const path = currentPlan === 'pro'
      ? `/api/admin/users/${uid}/revoke-pro`
      : `/api/admin/users/${uid}/grant-pro`;
    await authFetch(path, { method: 'POST' });
    load();
  };

  const max14 = stats?.users?.signups_series_14d?.reduce((m, d) => Math.max(m, d.count), 0) || 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-slate-400 hover:text-amber-400 flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to studio
            </Link>
            <span className="text-slate-600">/</span>
            <span className="font-semibold text-amber-400">Owner Dashboard</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 uppercase tracking-wider" title="All timestamps are shown in India Standard Time">
              All times · IST
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400 hidden sm:inline">{user?.email}</span>
            <button
              onClick={load}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-slate-800"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-xs text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 px-3 py-1 rounded"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Total users" value={stats?.users?.total ?? '—'} accent="amber" />
          <StatCard icon={Crown} label="Pro users" value={stats?.users?.pro ?? '—'} accent="violet" />
          <StatCard icon={TrendingUp} label="Signups (week)" value={stats?.users?.signups_week ?? '—'} accent="cyan" />
          <StatCard icon={Sparkles} label="AI calls (week)" value={stats?.ai_calls?.week ?? '—'} accent="emerald" />
        </div>

        {/* Conversion funnel (7d) */}
        {stats?.funnel_7d && (
          <div
            className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl p-5"
            data-testid="admin-funnel-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-400" />
                Conversion funnel — last 7 days
              </h2>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                Drop-off between stages
              </span>
            </div>
            {(() => {
              const f = stats.funnel_7d;
              const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);
              const stages = [
                { key: 'visitors', label: 'Visitors', value: f.visitors, accent: 'from-slate-500/20 to-slate-400/10 text-slate-200 border-slate-500/30' },
                { key: 'signups', label: 'Signups', value: f.signups, accent: 'from-cyan-500/20 to-sky-500/10 text-cyan-200 border-cyan-500/30', dropFrom: f.visitors },
                { key: 'paying', label: 'Paying', value: f.paying, accent: 'from-violet-500/20 to-purple-500/10 text-violet-200 border-violet-500/30', dropFrom: f.signups },
              ];
              return (
                <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-stretch">
                  {stages.map((s, idx) => (
                    <React.Fragment key={s.key}>
                      <div
                        className={`md:col-span-2 rounded-lg border bg-gradient-to-br ${s.accent} p-4`}
                        data-testid={`funnel-stage-${s.key}`}
                      >
                        <div className="text-[10px] uppercase tracking-wider opacity-70">{s.label}</div>
                        <div className="text-2xl font-bold text-white mt-1">{s.value}</div>
                        {idx > 0 && typeof s.dropFrom === 'number' && (
                          <div className="text-[11px] mt-1 opacity-80">
                            {pct(s.value, s.dropFrom)}% of {stages[idx - 1].label.toLowerCase()}
                          </div>
                        )}
                      </div>
                      {idx < stages.length - 1 && (
                        <div className="hidden md:flex items-center justify-center text-slate-500">
                          <ArrowRight className="w-5 h-5" />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              );
            })()}
            <div className="mt-4 pt-3 border-t border-slate-700/50 flex items-center justify-between text-sm">
              <span className="text-slate-400 text-xs uppercase tracking-wider">Revenue (7d)</span>
              <div className="flex items-center gap-4">
                <span className="text-emerald-300 font-bold">₹{stats.funnel_7d.revenue_inr.toLocaleString('en-IN')}</span>
                <span className="text-slate-600">·</span>
                <span className="text-violet-300 font-bold">${stats.funnel_7d.revenue_usd.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Exports KPI row (Phase 3 — free tier export gating) */}
        <div className="grid grid-cols-3 gap-3" data-testid="admin-exports-kpi-row">
          <StatCard icon={Download} label="Exports today" value={stats?.exports?.today ?? 0} accent="cyan" />
          <StatCard icon={Download} label="Exports this week" value={stats?.exports?.week ?? 0} accent="emerald" />
          <StatCard icon={Download} label="Exports this month" value={stats?.exports?.month ?? 0} accent="amber" />
        </div>

        {/* 14-day signup sparkline */}
        <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5 shadow-xl">
          <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-amber-400" />
            New signups — last 14 days
          </h2>
          <div className="flex items-end gap-1 h-28">
            {(stats?.users?.signups_series_14d || []).map((d) => (
              <div
                key={d.date}
                className="flex-1 bg-gradient-to-t from-amber-500 to-orange-400 rounded-t"
                style={{ height: `${(d.count / max14) * 100}%`, minHeight: d.count > 0 ? '4px' : '2px', opacity: d.count > 0 ? 1 : 0.15 }}
                title={`${d.date}: ${d.count}`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-slate-500 mt-2">
            <span>{stats?.users?.signups_series_14d?.[0]?.date}</span>
            <span>{stats?.users?.signups_series_14d?.[stats?.users?.signups_series_14d?.length - 1]?.date}</span>
          </div>
        </div>

        {/* Recent Payments */}
        <div
          className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden"
          data-testid="admin-recent-payments-panel"
        >
          <div className="p-5 pb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <IndianRupee className="w-4 h-4 text-emerald-400" />
              Recent payments ({recentPayments.length})
            </h2>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              Razorpay + Stripe · last {recentPayments.length || 10}
            </span>
          </div>
          {recentPayments.length === 0 ? (
            <div className="px-5 pb-5 text-sm text-slate-500" data-testid="admin-recent-payments-empty">
              No successful payments yet — your first paying customer will show up here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">When</th>
                    <th className="text-left px-4 py-2">Email</th>
                    <th className="text-left px-4 py-2">Package</th>
                    <th className="text-left px-4 py-2">Provider</th>
                    <th className="text-right px-4 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPayments.map((p, idx) => {
                    const isRzp = p.provider === 'razorpay';
                    const sym = (p.currency || '').toUpperCase() === 'INR' ? '₹' : '$';
                    const when = toIST(p.updated_at || p.created_at);
                    const ref = p.razorpay_payment_id || p.razorpay_order_id || p.session_id || '';
                    return (
                      <tr
                        key={(ref || '') + idx}
                        className="border-t border-slate-700/50 hover:bg-slate-700/20"
                        data-testid={`admin-recent-payment-row-${idx}`}
                      >
                        <td className="px-4 py-2 text-slate-400 text-xs whitespace-nowrap">{when}</td>
                        <td className="px-4 py-2 font-mono text-xs">{p.user_email || '—'}</td>
                        <td className="px-4 py-2 text-xs">{p.package_id}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                              isRzp
                                ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                : 'bg-violet-500/15 text-violet-300 border-violet-500/30'
                            }`}
                          >
                            {isRzp ? 'Razorpay' : 'Stripe'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-semibold whitespace-nowrap">
                          {sym}{Number(p.amount || 0).toFixed(isRzp ? 0 : 2)}
                          <span className="ml-1 text-[10px] text-slate-500">
                            {(p.currency || '').toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Registered users · time-on-site */}
        <div
          className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden"
          data-testid="admin-users-time-panel"
        >
          <div className="p-5 pb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              Registered users · total time on site ({usersTime.length})
            </h2>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              Longest-engaged first
            </span>
          </div>
          {usersTime.length === 0 ? (
            <div className="px-5 pb-5 text-sm text-slate-500" data-testid="admin-users-time-empty">
              No registered-user sessions yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">User</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">Plan</th>
                    <th className="text-right px-4 py-2">Total time</th>
                    <th className="text-right px-4 py-2 hidden md:table-cell">Sessions</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {usersTime.map((u, idx) => {
                    const secs = u.total_seconds || 0;
                    const hh = Math.floor(secs / 3600);
                    const mm = Math.floor((secs % 3600) / 60);
                    const ss = Math.floor(secs % 60);
                    const dur = hh > 0
                      ? `${hh}h ${String(mm).padStart(2, '0')}m`
                      : `${mm}m ${String(ss).padStart(2, '0')}s`;
                    const planCls = u.plan === 'pro'
                      ? 'bg-violet-500/15 text-violet-300 border-violet-500/30'
                      : u.plan === 'starter'
                      ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30'
                      : 'bg-slate-600/20 text-slate-400 border-slate-600/40';
                    return (
                      <tr
                        key={u.user_id || idx}
                        className="border-t border-slate-700/50 hover:bg-slate-700/20"
                        data-testid={`admin-users-time-row-${idx}`}
                      >
                        <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                        <td className="px-4 py-2 hidden md:table-cell">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${planCls}`}>
                            {u.plan}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-semibold text-amber-300 whitespace-nowrap">
                          {dur}
                        </td>
                        <td className="px-4 py-2 text-right hidden md:table-cell text-slate-400 text-xs">
                          {u.sessions_count}
                        </td>
                        <td className="px-4 py-2 hidden md:table-cell text-slate-400 text-xs whitespace-nowrap">
                          {u.last_seen_at ? toIST(u.last_seen_at) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Visitor Sessions */}
        <div
          className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden"
          data-testid="admin-sessions-panel"
        >
          <div className="p-5 pb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-400" />
              Visitor sessions ({sessions.length})
            </h2>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              Last {sessions.length || 100} · latest first
            </span>
          </div>
          {sessions.length === 0 ? (
            <div className="px-5 pb-5 text-sm text-slate-500" data-testid="admin-sessions-empty">
              No visitor sessions yet — hits start appearing as soon as someone opens the app.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">User / ID</th>
                    <th className="text-left px-4 py-2 hidden md:table-cell">IP</th>
                    <th className="text-left px-4 py-2">Visit date</th>
                    <th className="text-right px-4 py-2">Duration (HH:MM)</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s, idx) => {
                    const started = s.started_at ? new Date(s.started_at) : null;
                    const ended = s.last_seen_at ? new Date(s.last_seen_at) : null;
                    const secs = started && ended ? Math.max(0, Math.round((ended - started) / 1000)) : 0;
                    const hh = String(Math.floor(secs / 3600)).padStart(2, '0');
                    const mm = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
                    const duration = `${hh}:${mm}`;
                    const isAnon = s.user_id === 'anonymous';
                    const label = isAnon
                      ? `anon · ${(s.session_id || '').slice(0, 8)}`
                      : (s.email || s.user_id);
                    const visitDate = toIST(started);
                    return (
                      <tr
                        key={s.session_id || idx}
                        className="border-t border-slate-700/50 hover:bg-slate-700/20"
                        data-testid={`admin-session-row-${idx}`}
                      >
                        <td className="px-4 py-2 font-mono text-xs">
                          {label}
                          {isAnon && (
                            <span className="ml-2 text-[10px] text-slate-500">anonymous</span>
                          )}
                        </td>
                        <td className="px-4 py-2 hidden md:table-cell text-slate-400 text-xs">{s.ip || '—'}</td>
                        <td className="px-4 py-2 text-slate-400 text-xs whitespace-nowrap">{visitDate}</td>
                        <td className="px-4 py-2 text-right font-semibold whitespace-nowrap">{duration}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pricing editor + CSV export — owner controls */}
        {stats?.plan_config && (
          <PricingEditor
            initial={stats.plan_config}
            onSaved={(updated) => {
              setStats((s) => ({ ...s, plan_config: updated }));
            }}
          />
        )}

        <ExportDataPanel />

        <CelebrationConfigCard />

        {/* User Feedback */}
        <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden" data-testid="admin-feedback-panel">
          <div className="p-5 pb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-amber-400" />
              User feedback ({feedback.count})
            </h2>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-4 h-4 ${
                      n <= Math.round(feedback.avg)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-slate-600'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-slate-300 font-semibold">
                {feedback.count > 0 ? feedback.avg.toFixed(1) : '—'}
              </span>
            </div>
          </div>
          {feedback.count === 0 ? (
            <div className="px-5 pb-5 text-sm text-slate-500">No feedback yet.</div>
          ) : (
            <>
              <div className="px-5 pb-3 grid grid-cols-5 gap-1 text-[10px]">
                {[5, 4, 3, 2, 1].map((n) => {
                  const c = feedback.distribution[String(n)] || 0;
                  const pct = feedback.count ? Math.round((c / feedback.count) * 100) : 0;
                  return (
                    <div key={n} className="flex flex-col items-center text-slate-500" title={`${c} ratings`}>
                      <div className="w-full h-12 bg-slate-900/60 rounded relative overflow-hidden">
                        <div
                          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-amber-500 to-orange-400"
                          style={{ height: `${pct}%` }}
                        />
                      </div>
                      <span className="mt-1">{n}★ · {c}</span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-slate-700/50 max-h-96 overflow-y-auto">
                {feedback.items.map((f, idx) => (
                  <div key={idx} className="px-5 py-3 border-b border-slate-700/30 last:border-b-0" data-testid={`admin-feedback-item-${idx}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`w-3 h-3 ${n <= f.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`}
                          />
                        ))}
                        <span className="ml-2 text-xs text-slate-500">
                          {f.user_email || 'anonymous'}
                          {f.user_plan && <span className="ml-1 opacity-70">· {f.user_plan}</span>}
                          <span className="ml-1 opacity-50">· {f.context}</span>
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-600">
                        {f.created_at ? toIST(f.created_at) : ''}
                      </span>
                    </div>
                    {f.comment && (
                      <p className="text-sm text-slate-300 whitespace-pre-wrap">{f.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Users table */}
        <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-xl overflow-hidden">
          <h2 className="text-sm font-semibold text-slate-300 p-5 pb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-amber-400" />
            All users ({users.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Name</th>
                  <th className="text-left px-4 py-2">Plan</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Provider</th>
                  <th className="text-left px-4 py-2 hidden lg:table-cell">Joined</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-slate-700/50 hover:bg-slate-700/20" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{u.email} {u.is_admin && <span className="ml-1 text-amber-400">★</span>}</td>
                    <td className="px-4 py-2 hidden md:table-cell">{u.name}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.plan === 'pro' ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' : 'bg-slate-700 text-slate-400'}`}>
                        {u.plan}
                      </span>
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell text-slate-400">{u.auth_provider}</td>
                    <td className="px-4 py-2 hidden lg:table-cell text-slate-500 text-xs">
                      {u.created_at ? toISTDate(u.created_at) : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!u.is_admin && (
                        <button
                          onClick={() => togglePro(u.user_id, u.plan)}
                          data-testid={`admin-toggle-pro-${u.user_id}`}
                          className={`text-xs px-2 py-1 rounded ${u.plan === 'pro' ? 'bg-slate-700 hover:bg-slate-600' : 'bg-violet-600 hover:bg-violet-700'} text-white`}
                        >
                          {u.plan === 'pro' ? 'Revoke Pro' : 'Grant Pro'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No users yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
